import React, { useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import styles from "./CustomizeBanner.module.css";

import PageHeader from "NewsAdmin/Shared/PageHeader";
import FeedBanner from "NewsAdmin/Shared/FeedBanner";
import BannerDropdownList from "NewsAdmin/Shared/BannerDropdownList";
import FeedItemPreview from "NewsAdmin/Shared/FeedItemPreview";
import DeleteFeedItemBar from "NewsAdmin/Shared/DeleteFeedItemBar";

import { useEditFeedPage } from "../EditFeedPage/useEditFeedPage";
import Loader from "shared/Loader";
import dayjs from "dayjs";
import { useFeedItemOptionsDropdown } from "NewsAdmin/Shared/FeedItemOptionsDropdown/useFeedItemOptionsDropdown";
import NotificationOverlay from "NewsAdmin/Shared/NotificationOverlay";
import ConfirmationDialog from "NewsAdmin/Shared/ConfirmationDialog";
import moment from "moment-timezone";

// utils
const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export function CustomizeBanner({
  isNew,
  isCloning = false,
  justAdded = false,
  feedItem: incomingFeedItem,
  feedItemId,
  setPage,
  diamondsList,
  groupsList
}) {
  const history = useHistory();
  const queryClient = useQueryClient();

  // default structure for Banner
  const defaultFeedItem = {
    type: "Banner",
    options: {
      title: "* Banner Title *",
      type: "banner",
      previewIBO: 0,
      pinLevelPermissions: ["AssociateIbo", "Platinum", "Emerald", "Diamond"],
      familyReunions: [],
      diamonds: [],
      groups: [],
      platforms: ["Web", "iOS", "Android"],
      shouldSendUpdateIcon: true,
      status: 0,
      availableOn: moment()
        .hour(17)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate(),
      unpublishOn: "",
      unpublishType: 0,
      isEditingDraft: false,
      managementApproved: false,
      group: 2,
      priority: 5,
    },
    information: {
      link: "",
      img: "",
      align: "center",
    },
  };

  // merge defaults with incoming data
  // const mergedInitial = deepClone({
  //   ...defaultFeedItem,
  //   ...incomingFeedItem,
  //   options: {
  //     ...defaultFeedItem.options,
  //     ...incomingFeedItem?.options,
  //   },
  //   information: {
  //     ...defaultFeedItem.information,
  //     ...incomingFeedItem?.information,
  //   },
  //   template: incomingFeedItem?.template || "Banner",
  // });
  const mergedInitial = deepClone({
    ...(isNew ? defaultFeedItem : incomingFeedItem),
  });

  const [feedItem, setFeedItem] = useState(deepClone(mergedInitial));
  const [initialFeedItem] = useState(deepClone(mergedInitial));
  const [hasChanged, setHasChanged] = useState(isNew);
  const [hasPersistedChangesOnServer, setHasPersistedChangesOnServer] =
    useState(Boolean(justAdded));
  const [isDraft, setIsDraft] = useState(
    feedItem.options.isEditingDraft || false
  );
  const [resetKey, setResetKey] = useState(0);

  const [showAlert, setShowAlert] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const triggerAlert = () => {
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 1000);
  };

  const {
    saveFeedItem,
    saveAsDraft,
    createFeedItem,
    deleteFeedItem,
  } = useEditFeedPage();

  // track changes
  useEffect(() => {
    setHasChanged(!isEqual(feedItem, initialFeedItem));
  }, [feedItem, initialFeedItem]);

  const onBackArrow = () => {
    if (!hasPersistedChangesOnServer) {
      history.push(`/marketing/news/feed`);
      return;
    }
    history.push(`/marketing/news/feed`, {
      showFeedLoader: true,
      expectedFeedId: feedItem?.options?.id || feedItemId || null,
      expectedFeedTitle: hasChanged ? null : feedItem?.options?.title || null,
    });
  };

  const onReset = () => {
    if (!isNew) {
      setFeedItem(deepClone(initialFeedItem));
      setResetKey((prev) => prev + 1);
      setHasChanged(false);
    } else {
      setPage("create");
    }
  };

  const onSave = () => {
    if (isNew || isCloning) {
      createFeedItem(
        { feedItem, diamondsList, groupsList },
        {
          onSuccess: (itemId) => {
            // const newFeedItem = deepClone({
            //   ...feedItem,
            //   id: itemId,
            // });

            // setFeedItem(newFeedItem);
            history.push(`/marketing/news/feed/${itemId}`, {
              pageType: "Banner",
              justAdded: true,
            });
            // history.push("/marketing/news/feed");
          },
          onError: (err) => {
            console.error("Create failed:", err);
          },
        }
      );
    } else {
      saveFeedItem(
        { feedItem, diamondsList, groupsList },
        {
          onSuccess: (updatedItem) => {
            const merged = deepClone({
              ...feedItem,
              ...updatedItem,
              options: {
                ...feedItem.options,
                ...updatedItem.options,
              },
              information: {
                ...feedItem.information,
                ...updatedItem.information,
              },
            });
            setFeedItem(merged);
            queryClient.setQueryData(["feedItem", updatedItem.id ?? updatedItem.Id], merged);
            queryClient.invalidateQueries(["feed"]);
            setIsDraft(false);
            setHasChanged(false);
            setHasPersistedChangesOnServer(true);
            triggerAlert();
          },
          onError: (err) => console.error("Save failed: " + err.message),
        }
      );
    }
  };

  const onDraft = () => {
    if (isNew || isCloning) {
      createFeedItem(
        { feedItem, diamondsList, groupsList, isDraft },
        {
          onSuccess: () => {
            history.push("/marketing/news/feed");
          },
          onError: (err) => {
            console.error("Create failed:", err);
          },
        }
      );
    } else {
      saveAsDraft(
        { feedItem, diamondsList, groupsList },
        {
          onSuccess: async (updatedItem) => {
            const merged = deepClone({
              ...feedItem,
              ...updatedItem,
              options: {
                ...feedItem.options,
                ...updatedItem.options,
              },
              information: {
                ...feedItem.information,
                ...updatedItem.information,
              },
            });
            setFeedItem(merged);
            queryClient.setQueryData(["feedItem", updatedItem.id ?? updatedItem.Id], merged);
            queryClient.invalidateQueries(["feed"]);

            setIsDraft(true);
            setHasChanged(false);
            setHasPersistedChangesOnServer(true);
            triggerAlert();
          },
          onError: (err) => console.error("Draft failed: " + err.message),
        }
      );
    }
  };

  const onDelete = () => {
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = () => {
    setShowDeleteConfirm(false);
    deleteFeedItem(
      { id: feedItem.options.id || feedItemId },
      {
        onSuccess: () => {
          history.push("/marketing/news/feed");
        },
        onError: (err) => {
          console.error("Delete failed:", err);
        },
      }
    );
  };

  if (!feedItem) {
    return <Loader />;
  }

  return (
    <div>
      <ConfirmationDialog
        isVisible={showDeleteConfirm}
        confirmationText="Are you sure you want to delete this feed item? This action cannot be undone."
        onConfirmation={onConfirmDelete}
        onDismiss={() => setShowDeleteConfirm(false)}
      />
      <PageHeader onClick={onBackArrow} isHome={false} />
      <FeedBanner
        feedType={feedItem.type}
        feedTitle={feedItem.options.title}
        status={isNew ? "New" : isCloning ? "Clone" : "Edit"}
        isChangedOrNew={isNew || hasChanged}
        isDraft={isDraft}
        showDraft={!isNew && !isCloning}
        onCancel={onBackArrow}
        onReset={onReset}
        onSave={onSave}
        onDraft={onDraft}
      />

      <div className={styles.container}>
        <div className={styles.dropdown} key={resetKey}>
          <BannerDropdownList
            optionsConfig={feedItem.options}
            setOptionsConfig={(opts) =>
              setFeedItem((prev) => ({
                ...prev,
                options: { ...prev.options, ...opts },
              }))
            }
            bannerInformation={feedItem.information}
            setBannerInformation={(info) =>
              setFeedItem((prev) => ({
                ...prev,
                information: { ...prev.information, ...info },
              }))
            }
            isNew={isNew}
          />
        </div>

        <div className={styles.preview}>
          <FeedItemPreview feedItem={feedItem} />
        </div>
      </div>

      {!isDraft && !isNew ? (
        <DeleteFeedItemBar onClick={onDelete} />
      ) : (
        <div className={styles.emptyFooterBar} />
      )}

      <NotificationOverlay
        isVisible={showAlert}
        overlayText="Feed Item Updated"
      />
    </div>
  );
}
